import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LogOut } from 'lucide-react';
import hrService from '../../services/hrService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const today = new Date().toISOString().split('T')[0];
const currentMonth = new Date().getMonth() + 1;
const currentYear = new Date().getFullYear();

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const MODULES = ['Dairy', 'Agri', 'Adhesive', 'Chemical'];

const formatCurrency = (amount) =>
  `Rs ${parseFloat(amount || 0).toLocaleString('en-PK', { minimumFractionDigits: 0 })}`;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-PK', { year: 'numeric', month: 'short', day: 'numeric' });
};

const HRDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('employees');
  const [activeModule, setActiveModule] = useState('Dairy');
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [advanceMonth, setAdvanceMonth] = useState('newest');
  const [salaryMonth, setSalaryMonth] = useState('newest');
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(false);

  // Profile & Ledger
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileEmployee, setProfileEmployee] = useState(null);
  const [profileLedger, setProfileLedger] = useState(null);
  const [profileLoans, setProfileLoans] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerStartDate, setLedgerStartDate] = useState('');
  const [ledgerEndDate, setLedgerEndDate] = useState('');

  // Modals
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showEditEmployee, setShowEditEmployee] = useState(false);
  const [showAddAdvance, setShowAddAdvance] = useState(false);
  const [showAddSalary, setShowAddSalary] = useState(false);
  const [showAddLoan, setShowAddLoan] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [employeeForm, setEmployeeForm] = useState({
    name: '', id_card: '', phone: '', emergency_contact: '', address: '',
    last_job: '', reference: '', experience: '', monthly_salary: '',
    joining_date: today, notes: '', module: 'Dairy', permanent_loan: ''
  });

  const [advanceForm, setAdvanceForm] = useState({
    employee_id: '', amount: '', reason: '', given_date: today
  });

  const [salaryForm, setSalaryForm] = useState({
    employee_id: '', month: currentMonth, year: currentYear,
    basic_salary: '', advance_deduction: '', bonus: '', paid_date: today, notes: ''
  });

  const [loanForm, setLoanForm] = useState({
    employee_id: '', amount: '', reason: '', given_date: today, notes: ''
  });

  // Module allocation — splits one employee's salary across several modules.
  const [allocations, setAllocations] = useState([]);

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    const filtered = employees.filter(e => e.module === activeModule);
    setFilteredEmployees(filtered);
  }, [activeModule, employees]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [empRes, advRes, salRes] = await Promise.all([
        hrService.getAllEmployees(),
        hrService.getAllAdvances(),
        hrService.getAllSalaries(),
      ]);
      setEmployees(empRes.data || []);
      setAdvances(advRes.data || []);
      setSalaries(salRes.data || []);
    } catch (e) {
      console.error('HR fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  const openProfileModal = async (emp) => {
    setProfileEmployee(emp);
    setShowProfileModal(true);
    setLedgerLoading(true);
    try {
      const [ledgerRes, loansRes] = await Promise.all([
        hrService.getEmployeeFullLedger(emp.id),
        hrService.getPermanentLoansByEmployee(emp.id),
      ]);
      setProfileLedger(ledgerRes.data);
      setProfileLoans(loansRes.data || []);
    } catch (e) {
      console.error('Error loading profile:', e);
    } finally {
      setLedgerLoading(false);
    }
  };

  const fetchLedgerWithDates = async () => {
    if (!profileEmployee) return;
    setLedgerLoading(true);
    try {
      const res = await hrService.getEmployeeFullLedger(profileEmployee.id, ledgerStartDate, ledgerEndDate);
      setProfileLedger(res.data);
    } catch (e) {
      console.error('Error fetching ledger:', e);
    } finally {
      setLedgerLoading(false);
    }
  };

  // MODULE ALLOCATIONS

  const allocTotal = allocations.reduce((s, a) => s + (parseFloat(a.percent) || 0), 0);
  const allocValid = allocations.length === 0 || Math.abs(allocTotal - 100) < 0.01;

  const addAllocRow = () => {
    const used = allocations.map(a => a.module);
    const next = MODULES.find(m => !used.includes(m));
    if (!next) return;
    setAllocations([...allocations, { module: next, percent: '' }]);
  };

  const updateAllocRow = (idx, field, value) => {
    const copy = [...allocations];
    copy[idx] = { ...copy[idx], [field]: value };
    setAllocations(copy);
  };

  const removeAllocRow = (idx) => setAllocations(allocations.filter((_, i) => i !== idx));

  // Sending an empty list clears the rows, which makes the employee fall back to
  // their single `module` column — exactly how everyone behaved before this feature.
  const saveAllocations = async (employeeId) => {
    const clean = allocations
      .filter(a => a.module && parseFloat(a.percent) > 0)
      .map(a => ({ module: a.module, percent: parseFloat(a.percent) }));
    await hrService.setEmployeeAllocations(employeeId, clean);
  };

  const renderAllocationBlock = () => (
    <div style={{ gridColumn: '1 / -1', marginTop: '8px', padding: '14px', background: '#f8f9fb', border: '2px solid #e0e0e0', borderRadius: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <label style={{ ...labelStyle, marginBottom: 0, marginTop: 0 }}>Salary Split Across Modules</label>
        <button type="button" onClick={addAllocRow} disabled={allocations.length >= MODULES.length}
          style={{ padding: '6px 12px', background: allocations.length >= MODULES.length ? '#bdc3c7' : '#667eea', color: 'white', border: 'none', borderRadius: '6px', cursor: allocations.length >= MODULES.length ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '600' }}>
          + Add Module
        </button>
      </div>

      {allocations.length === 0 ? (
        <div style={{ fontSize: '12px', color: '#7f8c8d', lineHeight: '1.5' }}>
          No split set — the full salary posts to the <strong>{employeeForm.module}</strong> module above.
          Add rows only if this employee works across multiple modules.
        </div>
      ) : (
        <>
          {allocations.map((a, idx) => (
            <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
              <select value={a.module} onChange={e => updateAllocRow(idx, 'module', e.target.value)}
                style={{ ...inputStyle, marginBottom: 0, flex: 2 }}>
                {MODULES.map(m => (
                  <option key={m} value={m}
                    disabled={allocations.some((x, i) => i !== idx && x.module === m)}>{m}</option>
                ))}
              </select>
              <input type="number" min="0" max="100" step="0.01" placeholder="%" value={a.percent}
                onChange={e => updateAllocRow(idx, 'percent', e.target.value)}
                style={{ ...inputStyle, marginBottom: 0, flex: 1 }} />
              <span style={{ fontSize: '14px', color: '#555', fontWeight: '600' }}>%</span>
              <button type="button" onClick={() => removeAllocRow(idx)}
                style={{ padding: '8px 12px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                ✕
              </button>
            </div>
          ))}
          <div style={{ marginTop: '10px', padding: '8px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: '700',
            background: allocValid ? '#e8f8f0' : '#fdecea', color: allocValid ? '#1e8449' : '#c0392b' }}>
            Total: {allocTotal.toFixed(2)}% {allocValid ? '✓' : '— must be exactly 100%'}
          </div>
          {allocValid && employeeForm.monthly_salary > 0 && (
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#555', lineHeight: '1.6' }}>
              {allocations.filter(a => parseFloat(a.percent) > 0).map((a, i) => (
                <div key={i}>
                  {a.module}: {formatCurrency(parseFloat(employeeForm.monthly_salary) * parseFloat(a.percent) / 100)}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <div style={{ marginTop: '10px', fontSize: '11px', color: '#e67e22' }}>
        Note: the Chemical module is not yet wired — its share will be skipped until that work is done.
      </div>
    </div>
  );

  // EMPLOYEES

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    if (!allocValid) {
      alert(`Salary split must total exactly 100%. Current total: ${allocTotal.toFixed(2)}%`);
      return;
    }
    try {
      // The allocation rows need the new employee's id, so they are saved on a
      // second call once the insert returns.
      const res = await hrService.createEmployee(employeeForm);
      if (res?.data?.id && allocations.length > 0) await saveAllocations(res.data.id);
      alert('Employee added successfully!');
      setShowAddEmployee(false);
      resetEmployeeForm();
      fetchAll();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || 'Failed to add employee'));
    }
  };

  const handleEditEmployee = async (e) => {
    e.preventDefault();
    if (!allocValid) {
      alert(`Salary split must total exactly 100%. Current total: ${allocTotal.toFixed(2)}%`);
      return;
    }
    try {
      await hrService.updateEmployee(selectedEmployee.id, employeeForm);
      await saveAllocations(selectedEmployee.id);
      alert('Employee updated successfully!');
      setShowEditEmployee(false);
      setSelectedEmployee(null);
      resetEmployeeForm();
      fetchAll();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || 'Failed to update employee'));
    }
  };

  const handleDeleteEmployee = async (id, name) => {
    if (!window.confirm(`Permanently delete "${name}"? This will delete all their records.`)) return;
    try {
      await hrService.deleteEmployee(id);
      alert('Employee deleted!');
      fetchAll();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || 'Failed to delete employee'));
    }
  };

  const openEditEmployee = async (emp) => {
    setSelectedEmployee(emp);
    try {
      const res = await hrService.getEmployeeAllocations(emp.id);
      setAllocations((res.data || []).map(a => ({ module: a.module, percent: String(a.percent) })));
    } catch {
      setAllocations([]);
    }
    setEmployeeForm({
      name: emp.name || '',
      id_card: emp.id_card || '',
      phone: emp.phone || '',
      emergency_contact: emp.emergency_contact || '',
      address: emp.address || '',
      last_job: emp.last_job || '',
      reference: emp.reference || '',
      experience: emp.experience || '',
      monthly_salary: emp.monthly_salary || '',
      joining_date: emp.joining_date?.split('T')[0] || today,
      notes: emp.notes || '',
      module: emp.module || 'Dairy',
      permanent_loan: emp.permanent_loan || '',
    });
    setShowEditEmployee(true);
  };

 const resetEmployeeForm = () => {
    setEmployeeForm({
      name: '', id_card: '', phone: '', emergency_contact: '', address: '',
      last_job: '', reference: '', experience: '', monthly_salary: '',
      joining_date: today, notes: '', module: 'Dairy', permanent_loan: ''
    });
    setAllocations([]);
  };

  // ADVANCES

  const handleAddAdvance = async (e) => {
    e.preventDefault();
    try {
      await hrService.createAdvance(advanceForm);
      alert('Advance recorded!');
      setShowAddAdvance(false);
      setAdvanceForm({ employee_id: '', amount: '', reason: '', given_date: today });
      fetchAll();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || 'Failed to record advance'));
    }
  };

  const handleDeleteAdvance = async (id) => {
    if (!window.confirm('Delete this advance record?')) return;
    try {
      await hrService.deleteAdvance(id);
      alert('Advance deleted!');
      fetchAll();
    } catch (err) {
      alert('Failed to delete advance');
    }
  };

  // SALARIES

  const handleAddSalary = async (e) => {
    e.preventDefault();
    try {
      await hrService.createSalary(salaryForm);
      alert('Salary recorded!');
      setShowAddSalary(false);
      setSalaryForm({ employee_id: '', month: currentMonth, year: currentYear, basic_salary: '', advance_deduction: '', bonus: '', paid_date: today, notes: '' });
      fetchAll();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || 'Failed to record salary'));
    }
  };

  const handleDeleteSalary = async (id) => {
    if (!window.confirm('Delete this salary record?')) return;
    try {
      await hrService.deleteSalary(id);
      alert('Salary record deleted!');
      fetchAll();
    } catch (err) {
      alert('Failed to delete salary');
    }
  };

  // PERMANENT LOANS

  const handleAddLoan = async (e) => {
    e.preventDefault();
    try {
      await hrService.createPermanentLoan(loanForm);
      alert('Permanent loan recorded!');
      setShowAddLoan(false);
      setLoanForm({ employee_id: '', amount: '', reason: '', given_date: today, notes: '' });
      fetchAll();
      if (profileEmployee) {
        const loansRes = await hrService.getPermanentLoansByEmployee(profileEmployee.id);
        setProfileLoans(loansRes.data || []);
      }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || 'Failed to record loan'));
    }
  };

  const handleSettleLoan = async (loanId) => {
    if (!window.confirm('Mark this loan as settled? This means the employee has repaid it on leaving.')) return;
    try {
      await hrService.settlePermanentLoan(loanId);
      alert('Loan marked as settled!');
      if (profileEmployee) {
        const loansRes = await hrService.getPermanentLoansByEmployee(profileEmployee.id);
        setProfileLoans(loansRes.data || []);
      }
    } catch (err) {
      alert('Failed to settle loan');
    }
  };

  const handleDeleteLoan = async (loanId) => {
    if (!window.confirm('Delete this loan record?')) return;
    try {
      await hrService.deletePermanentLoan(loanId);
      alert('Loan deleted!');
      if (profileEmployee) {
        const loansRes = await hrService.getPermanentLoansByEmployee(profileEmployee.id);
        setProfileLoans(loansRes.data || []);
      }
    } catch (err) {
      alert('Failed to delete loan');
    }
  };

  // PDF SALARY SLIP

  const generateSalarySlip = (salary) => {
    const emp = employees.find(e => e.id === salary.employee_id);
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFillColor(44, 62, 80);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('SOHAIB ENTERPRISES', pageWidth / 2, 16, { align: 'center' });
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('SALARY SLIP', pageWidth / 2, 26, { align: 'center' });
    doc.setFontSize(10);
    doc.text(`${MONTHS[salary.month - 1]} ${salary.year}`, pageWidth / 2, 34, { align: 'center' });

    doc.setTextColor(44, 62, 80);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Employee Details', 14, 52);
    doc.setDrawColor(44, 62, 80);
    doc.line(14, 54, pageWidth - 14, 54);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Name: ${emp?.name || salary.employee_name || '-'}`, 14, 62);
    doc.text(`ID Card: ${emp?.id_card || '-'}`, 14, 70);
    doc.text(`Phone: ${emp?.phone || '-'}`, 14, 78);
    doc.text(`Joining Date: ${formatDate(emp?.joining_date)}`, pageWidth / 2, 62);
    doc.text(`Paid Date: ${formatDate(salary.paid_date)}`, pageWidth / 2, 70);
    doc.text(`Month: ${MONTHS[salary.month - 1]} ${salary.year}`, pageWidth / 2, 78);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Salary Breakdown', 14, 92);
    doc.line(14, 94, pageWidth - 14, 94);

    autoTable(doc, {
      startY: 96,
      head: [['Description', 'Amount']],
      body: [
        ['Basic Salary', formatCurrency(salary.basic_salary)],
        ['Bonus', formatCurrency(salary.bonus)],
        ['Advance Deduction', `- ${formatCurrency(salary.advance_deduction)}`],
      ],
      foot: [['NET SALARY', formatCurrency(salary.net_salary)]],
      styles: { fontSize: 11, cellPadding: 5 },
      headStyles: { fillColor: [44, 62, 80], textColor: 255 },
      footStyles: { fillColor: [27, 94, 32], textColor: 255, fontStyle: 'bold', fontSize: 12 },
      columnStyles: { 1: { halign: 'right' } },
    });

    const finalY = doc.lastAutoTable.finalY + 20;
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text('This is a computer generated salary slip.', pageWidth / 2, finalY, { align: 'center' });
    doc.text(`Generated on: ${new Date().toLocaleString('en-PK')}`, pageWidth / 2, finalY + 6, { align: 'center' });

    doc.save(`Salary_Slip_${emp?.name || 'Employee'}_${MONTHS[salary.month - 1]}_${salary.year}.pdf`);
  };

  // PDF EMPLOYEE REPORT

  const generateEmployeeReport = (emp, ledger, loans) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFillColor(44, 62, 80);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('SOHAIB ENTERPRISES', pageWidth / 2, 15, { align: 'center' });
    doc.setFontSize(13);
    doc.text('EMPLOYEE REPORT', pageWidth / 2, 26, { align: 'center' });
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-PK')}`, pageWidth / 2, 35, { align: 'center' });

    doc.setTextColor(44, 62, 80);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Employee Information', 14, 52);
    doc.line(14, 54, pageWidth - 14, 54);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Name: ${emp.name}`, 14, 62);
    doc.text(`ID Card: ${emp.id_card || '-'}`, 14, 70);
    doc.text(`Phone: ${emp.phone || '-'}`, 14, 78);
    doc.text(`Emergency Contact: ${emp.emergency_contact || '-'}`, 14, 86);
    doc.text(`Module: ${emp.module || '-'}`, pageWidth / 2, 62);
    doc.text(`Joining Date: ${formatDate(emp.joining_date)}`, pageWidth / 2, 70);
    doc.text(`Monthly Salary: ${formatCurrency(emp.monthly_salary)}`, pageWidth / 2, 78);
    doc.text(`Advance Balance: ${formatCurrency(emp.advance_balance)}`, pageWidth / 2, 86);

    let currentY = 100;

    if (loans && loans.length > 0) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Permanent Loans', 14, currentY);
      doc.line(14, currentY + 2, pageWidth - 14, currentY + 2);
      autoTable(doc, {
        startY: currentY + 6,
        head: [['Date', 'Amount', 'Reason', 'Status', 'Settled Date']],
        body: loans.map(l => [
          formatDate(l.given_date),
          formatCurrency(l.amount),
          l.reason || '-',
          l.status,
          l.settled_date ? formatDate(l.settled_date) : '-',
        ]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [142, 68, 173], textColor: 255 },
      });
      currentY = doc.lastAutoTable.finalY + 10;
    }

    if (ledger?.salaries && ledger.salaries.length > 0) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Salary History', 14, currentY);
      doc.line(14, currentY + 2, pageWidth - 14, currentY + 2);
      autoTable(doc, {
        startY: currentY + 6,
        head: [['Month', 'Basic', 'Bonus', 'Advance Deducted', 'Net Salary', 'Paid Date']],
        body: ledger.salaries.map(s => [
          `${MONTHS[s.month - 1]} ${s.year}`,
          formatCurrency(s.basic_salary),
          formatCurrency(s.bonus),
          formatCurrency(s.advance_deduction),
          formatCurrency(s.net_salary),
          formatDate(s.date),
        ]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [39, 174, 96], textColor: 255 },
      });
      currentY = doc.lastAutoTable.finalY + 10;
    }

    if (ledger?.advances && ledger.advances.length > 0) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Advance History', 14, currentY);
      doc.line(14, currentY + 2, pageWidth - 14, currentY + 2);
      autoTable(doc, {
        startY: currentY + 6,
        head: [['Date', 'Amount', 'Reason']],
        body: ledger.advances.map(a => [
          formatDate(a.date),
          formatCurrency(a.amount),
          a.reason || '-',
        ]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [231, 76, 60], textColor: 255 },
      });
    }

    doc.save(`Employee_Report_${emp.name}_${new Date().toLocaleDateString('en-PK').replace(/\//g, '-')}.pdf`);
  };

  // STATS

  const totalAdvanceBalance = employees.reduce((s, e) => s + parseFloat(e.advance_balance || 0), 0);
  const activeEmployees = employees.filter(e => e.is_active !== false).length;
  const thisMonthSalaries = salaries.filter(s => s.month === currentMonth && s.year === currentYear);
  const totalSalaryPaid = thisMonthSalaries.reduce((s, sal) => s + parseFloat(sal.net_salary || 0), 0);

  const tabs = [
    { id: 'employees', label: 'Employees', icon: '' },
    { id: 'advances', label: 'Advances', icon: '' },
    { id: 'salaries', label: 'Salaries', icon: '' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa', fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', padding: '20px 40px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/dashboard')} style={{ padding: '10px 20px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
              Back
            </button>
            <div>
              <h1 style={{ fontSize: '28px', margin: '0 0 4px 0', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '12px' }}>
                HR Management
              </h1>
              <p style={{ margin: 0, opacity: 0.9, fontSize: '14px' }}>Employees, Salaries & Advances</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ padding: '10px 16px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '10px', fontSize: '14px', fontWeight: '600' }}>
              {user?.full_name || user?.username || 'Admin'}
            </div>
            <button onClick={logout} style={{ padding: '12px 24px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '10px', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600' }}>
              <LogOut size={18} /> Logout
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '20px' }}>
          {[
            { label: 'Total Employees', value: employees.length },
            { label: 'Active', value: activeEmployees },
            { label: 'Total Advance Balance', value: formatCurrency(totalAdvanceBalance) },
            { label: 'This Month Salary', value: formatCurrency(totalSalaryPaid) },
          ].map((stat, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '10px', padding: '12px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: '700' }}>{stat.value}</div>
              <div style={{ fontSize: '11px', opacity: 0.9, textTransform: 'uppercase' }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '16px', flexWrap: 'wrap' }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ padding: '10px 20px', background: activeTab === tab.id ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)', border: activeTab === tab.id ? '2px solid rgba(255,255,255,0.5)' : '2px solid transparent', borderRadius: '10px', color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: activeTab === tab.id ? '700' : '500', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '40px' }}>

        {/* EMPLOYEES TAB */}
        {activeTab === 'employees' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: '#2c3e50', fontWeight: '700' }}>Employees</h2>
              <button onClick={() => { resetEmployeeForm(); setShowAddEmployee(true); }} style={{ padding: '10px 20px', background: '#667eea', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
                Add Employee
              </button>
            </div>

            {/* Module Filter Buttons */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
              {MODULES.map(mod => (
                <button key={mod} onClick={() => setActiveModule(mod)} style={{ padding: '10px 24px', background: activeModule === mod ? '#667eea' : 'white', color: activeModule === mod ? 'white' : '#667eea', border: '2px solid #667eea', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '14px' }}>
                  {mod} Emp
                </button>
              ))}
            </div>

            <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    {['Name', 'Phone', 'ID Card', 'Experience', 'Monthly Salary', 'Advance Balance', 'Permanent Loan', 'Joining Date', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#2c3e50', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.length === 0 ? (
                    <tr><td colSpan="10" style={{ padding: '40px', textAlign: 'center', color: '#95a5a6' }}>No employees found for {activeModule} module</td></tr>
                  ) : filteredEmployees.map(emp => (
                    <tr key={emp.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '14px 16px', fontWeight: '600' }}>
                        <span onClick={() => openProfileModal(emp)} style={{ cursor: 'pointer', color: '#667eea', textDecoration: 'underline' }}>
                          {emp.name}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '13px' }}>{emp.phone || '-'}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px' }}>{emp.id_card || '-'}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px' }}>{emp.experience || '-'}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#27ae60', fontWeight: '600' }}>{formatCurrency(emp.monthly_salary)}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: parseFloat(emp.advance_balance) > 0 ? '#e74c3c' : '#27ae60', fontWeight: '600' }}>{formatCurrency(emp.advance_balance)}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#8e44ad', fontWeight: '600' }}>
                        {parseFloat(emp.permanent_loan || 0) > 0 ? formatCurrency(emp.permanent_loan) : '-'}
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '13px' }}>{formatDate(emp.joining_date)}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: emp.is_active !== false ? '#d4edda' : '#f8d7da', color: emp.is_active !== false ? '#155724' : '#721c24' }}>
                          {emp.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => openEditEmployee(emp)} style={{ padding: '5px 10px', background: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Edit</button>
                          <button onClick={() => handleDeleteEmployee(emp.id, emp.name)} style={{ padding: '5px 10px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Del</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ADVANCES TAB */}
        {activeTab === 'advances' && (() => {
          const yr = currentYear;
          const monthOptions = [{ value: 'all', label: 'All Months' }];
          for (let m = 0; m < 12; m++) {
            monthOptions.push({ value: `${yr}-${String(m + 1).padStart(2, '0')}`, label: `${MONTHS[m]} ${yr}` });
          }

          const dataKeys = new Set(
            advances.map(a => {
              const d = a.given_date ? new Date(a.given_date) : null;
              return d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : null;
            }).filter(Boolean)
          );

          let selected = advanceMonth;
          if (selected === 'newest') {
            const sortedData = Array.from(dataKeys).sort((a, b) => (a < b ? 1 : -1));
            selected = sortedData[0] || 'all';
          }

          const groupByMonth = (list) => {
            const groups = {};
            list.forEach(adv => {
              const d = adv.given_date ? new Date(adv.given_date) : null;
              const key = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : 'unknown';
              if (!groups[key]) groups[key] = [];
              groups[key].push(adv);
            });
            return groups;
          };

          const singleMonthRows = advances
            .filter(adv => {
              const d = adv.given_date ? new Date(adv.given_date) : null;
              const key = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : 'unknown';
              return key === selected;
            })
            .sort((a, b) => new Date(b.given_date) - new Date(a.given_date));

          const singleMonthTotal = singleMonthRows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);
          const selectedLabel = monthOptions.find(o => o.value === selected)?.label || '';

          return (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <h2 style={{ margin: 0, fontSize: '20px', color: '#2c3e50', fontWeight: '700' }}>Advance Records</h2>
                <button onClick={() => setShowAddAdvance(true)} style={{ padding: '10px 20px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
                  Give Advance
                </button>
              </div>

              {/* Month dropdown */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '13px', fontWeight: '700', color: '#555' }}>Month:</label>
                <select
                  value={selected}
                  onChange={e => setAdvanceMonth(e.target.value)}
                  style={{ padding: '10px 14px', border: '2px solid #667eea', borderRadius: '8px', fontSize: '14px', fontWeight: '600', color: '#2c3e50', minWidth: '200px' }}
                >
                  {monthOptions.map(o => (
                    <option key={o.value} value={o.value}>
                      {o.label}{o.value !== 'all' && dataKeys.has(o.value) ? ' *' : ''}
                    </option>
                  ))}
                </select>

                {selected !== 'all' && (
                  <div style={{ background: '#fff5f5', border: '1px solid #ef9a9a', borderRadius: '8px', padding: '8px 16px', fontSize: '14px', fontWeight: '700', color: '#e74c3c' }}>
                    {selectedLabel} Total: {formatCurrency(singleMonthTotal)}
                  </div>
                )}
              </div>

              <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      {['Employee', 'Amount', 'Reason', 'Date', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#2c3e50', borderBottom: '2px solid #e0e0e0' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {advances.length === 0 ? (
                      <tr><td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#95a5a6' }}>No advance records</td></tr>
                    ) : selected === 'all' ? (
                      (() => {
                        const groups = groupByMonth(advances);
                        const sortedKeys = Object.keys(groups).sort((a, b) => (a < b ? 1 : -1));
                        return sortedKeys.map(key => {
                          const rows = groups[key].slice().sort((a, b) => new Date(b.given_date) - new Date(a.given_date));
                          const monthTotal = rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);
                          const [gy, gm] = key.split('-');
                          const monthLabel = key === 'unknown' ? 'Undated' : `${MONTHS[parseInt(gm, 10) - 1]} ${gy}`;
                          return (
                            <React.Fragment key={key}>
                              <tr>
                                <td colSpan="4" style={{ padding: '10px 16px', fontWeight: '700', color: '#2c3e50', fontSize: '13px', background: '#eef1f8' }}>{monthLabel}</td>
                                <td style={{ padding: '10px 16px', fontWeight: '700', color: '#e74c3c', fontSize: '13px', background: '#eef1f8', whiteSpace: 'nowrap' }}>Total: {formatCurrency(monthTotal)}</td>
                              </tr>
                              {rows.map(adv => (
                                <tr key={adv.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                  <td style={{ padding: '14px 16px', fontWeight: '600' }}>{adv.employee_name}</td>
                                  <td style={{ padding: '14px 16px', color: '#e74c3c', fontWeight: '600' }}>{formatCurrency(adv.amount)}</td>
                                  <td style={{ padding: '14px 16px', fontSize: '13px' }}>{adv.reason || '-'}</td>
                                  <td style={{ padding: '14px 16px', fontSize: '13px' }}>{formatDate(adv.given_date)}</td>
                                  <td style={{ padding: '14px 16px' }}>
                                    <button onClick={() => handleDeleteAdvance(adv.id)} style={{ padding: '5px 10px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Delete</button>
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        });
                      })()
                    ) : singleMonthRows.length === 0 ? (
                      <tr><td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#95a5a6' }}>No advances for {selectedLabel}</td></tr>
                    ) : (
                      singleMonthRows.map(adv => (
                        <tr key={adv.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '14px 16px', fontWeight: '600' }}>{adv.employee_name}</td>
                          <td style={{ padding: '14px 16px', color: '#e74c3c', fontWeight: '600' }}>{formatCurrency(adv.amount)}</td>
                          <td style={{ padding: '14px 16px', fontSize: '13px' }}>{adv.reason || '-'}</td>
                          <td style={{ padding: '14px 16px', fontSize: '13px' }}>{formatDate(adv.given_date)}</td>
                          <td style={{ padding: '14px 16px' }}>
                            <button onClick={() => handleDeleteAdvance(adv.id)} style={{ padding: '5px 10px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Delete</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* SALARIES TAB */}
        {activeTab === 'salaries' && (() => {
          const yr = currentYear;
          const monthOptions = [{ value: 'all', label: 'All Months' }];
          for (let m = 0; m < 12; m++) {
            monthOptions.push({ value: `${yr}-${String(m + 1).padStart(2, '0')}`, label: `${MONTHS[m]} ${yr}` });
          }

          const salKey = (sal) => `${sal.year}-${String(sal.month).padStart(2, '0')}`;

          const dataKeys = new Set(salaries.map(salKey).filter(Boolean));

          let selected = salaryMonth;
          if (selected === 'newest') {
            const sortedData = Array.from(dataKeys).sort((a, b) => (a < b ? 1 : -1));
            selected = sortedData[0] || 'all';
          }

          const groupByMonth = (list) => {
            const groups = {};
            list.forEach(sal => {
              const key = salKey(sal);
              if (!groups[key]) groups[key] = [];
              groups[key].push(sal);
            });
            return groups;
          };

          const singleMonthRows = salaries
            .filter(sal => salKey(sal) === selected)
            .sort((a, b) => new Date(b.paid_date) - new Date(a.paid_date));

          const singleMonthTotal = singleMonthRows.reduce((s, r) => s + parseFloat(r.net_salary || 0), 0);
          const selectedLabel = monthOptions.find(o => o.value === selected)?.label || '';

          const salaryRow = (sal) => (
            <tr key={sal.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '14px 16px', fontWeight: '600' }}>{sal.employee_name}</td>
              <td style={{ padding: '14px 16px', fontSize: '13px' }}>{MONTHS[sal.month - 1]} {sal.year}</td>
              <td style={{ padding: '14px 16px', fontSize: '13px' }}>{formatCurrency(sal.basic_salary)}</td>
              <td style={{ padding: '14px 16px', fontSize: '13px', color: '#27ae60' }}>{formatCurrency(sal.bonus)}</td>
              <td style={{ padding: '14px 16px', fontSize: '13px', color: '#e74c3c' }}>{formatCurrency(sal.advance_deduction)}</td>
              <td style={{ padding: '14px 16px', fontWeight: '700', color: '#27ae60' }}>{formatCurrency(sal.net_salary)}</td>
              <td style={{ padding: '14px 16px', fontSize: '13px' }}>{formatDate(sal.paid_date)}</td>
              <td style={{ padding: '14px 16px' }}>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={() => generateSalarySlip(sal)} style={{ padding: '5px 10px', background: '#667eea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Slip</button>
                  <button onClick={() => handleDeleteSalary(sal.id)} style={{ padding: '5px 10px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Del</button>
                </div>
              </td>
            </tr>
          );

          return (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <h2 style={{ margin: 0, fontSize: '20px', color: '#2c3e50', fontWeight: '700' }}>Salary Records</h2>
                <button onClick={() => setShowAddSalary(true)} style={{ padding: '10px 20px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
                  Pay Salary
                </button>
              </div>

              {/* Month dropdown */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '13px', fontWeight: '700', color: '#555' }}>Month:</label>
                <select
                  value={selected}
                  onChange={e => setSalaryMonth(e.target.value)}
                  style={{ padding: '10px 14px', border: '2px solid #27ae60', borderRadius: '8px', fontSize: '14px', fontWeight: '600', color: '#2c3e50', minWidth: '200px' }}
                >
                  {monthOptions.map(o => (
                    <option key={o.value} value={o.value}>
                      {o.label}{o.value !== 'all' && dataKeys.has(o.value) ? ' *' : ''}
                    </option>
                  ))}
                </select>

                {selected !== 'all' && (
                  <div style={{ background: '#f0fff4', border: '1px solid #a5d6a7', borderRadius: '8px', padding: '8px 16px', fontSize: '14px', fontWeight: '700', color: '#27ae60' }}>
                    {selectedLabel} Total: {formatCurrency(singleMonthTotal)}
                  </div>
                )}
              </div>

              <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      {['Employee', 'Month', 'Basic', 'Bonus', 'Advance Deduction', 'Net Salary', 'Paid Date', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#2c3e50', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {salaries.length === 0 ? (
                      <tr><td colSpan="8" style={{ padding: '40px', textAlign: 'center', color: '#95a5a6' }}>No salary records</td></tr>
                    ) : selected === 'all' ? (
                      (() => {
                        const groups = groupByMonth(salaries);
                        const sortedKeys = Object.keys(groups).sort((a, b) => (a < b ? 1 : -1));
                        return sortedKeys.map(key => {
                          const rows = groups[key].slice().sort((a, b) => new Date(b.paid_date) - new Date(a.paid_date));
                          const monthTotal = rows.reduce((s, r) => s + parseFloat(r.net_salary || 0), 0);
                          const [gy, gm] = key.split('-');
                          const monthLabel = `${MONTHS[parseInt(gm, 10) - 1]} ${gy}`;
                          return (
                            <React.Fragment key={key}>
                              <tr>
                                <td colSpan="5" style={{ padding: '10px 16px', fontWeight: '700', color: '#2c3e50', fontSize: '13px', background: '#eef8f1' }}>{monthLabel}</td>
                                <td colSpan="3" style={{ padding: '10px 16px', fontWeight: '700', color: '#27ae60', fontSize: '13px', background: '#eef8f1', whiteSpace: 'nowrap' }}>Total: {formatCurrency(monthTotal)}</td>
                              </tr>
                              {rows.map(salaryRow)}
                            </React.Fragment>
                          );
                        });
                      })()
                    ) : singleMonthRows.length === 0 ? (
                      <tr><td colSpan="8" style={{ padding: '40px', textAlign: 'center', color: '#95a5a6' }}>No salaries for {selectedLabel}</td></tr>
                    ) : (
                      singleMonthRows.map(salaryRow)
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
      </div>

      {/* EMPLOYEE PROFILE MODAL */}
      {showProfileModal && profileEmployee && (
        <div style={modalOverlay} onClick={() => setShowProfileModal(false)}>
          <div style={{ ...modalBox, maxWidth: '750px' }} onClick={e => e.stopPropagation()}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <h2 style={{ ...modalTitle, marginBottom: '4px' }}>{profileEmployee.name}</h2>
                <span style={{ background: '#667eea', color: 'white', padding: '3px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>{profileEmployee.module} Module</span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => generateEmployeeReport(profileEmployee, profileLedger, profileLoans)} style={{ padding: '8px 16px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>PDF Report</button>
                <button onClick={() => { setShowProfileModal(false); openEditEmployee(profileEmployee); }} style={{ padding: '8px 16px', background: '#3498db', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>Edit</button>
                <button onClick={() => setShowProfileModal(false)} style={{ padding: '8px 16px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>Close</button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
              {[
                { label: 'ID Card', value: profileEmployee.id_card },
                { label: 'Phone', value: profileEmployee.phone },
                { label: 'Emergency Contact', value: profileEmployee.emergency_contact },
                { label: 'Joining Date', value: formatDate(profileEmployee.joining_date) },
                { label: 'Monthly Salary', value: formatCurrency(profileEmployee.monthly_salary) },
                { label: 'Advance Balance', value: formatCurrency(profileEmployee.advance_balance) },
                { label: 'Experience', value: profileEmployee.experience },
                { label: 'Last Job', value: profileEmployee.last_job },
                { label: 'Reference', value: profileEmployee.reference },
                { label: 'Address', value: profileEmployee.address },
              ].map((field, i) => (
                <div key={i} style={{ background: '#f8f9fa', borderRadius: '8px', padding: '10px 14px' }}>
                  <div style={{ fontSize: '11px', color: '#7f8c8d', textTransform: 'uppercase', fontWeight: '600', marginBottom: '3px' }}>{field.label}</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>{field.value || '-'}</div>
                </div>
              ))}
            </div>

            <div style={{ background: '#f0f4ff', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#667eea', marginBottom: '10px' }}>Filter Ledger by Date Range</div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="date" value={ledgerStartDate} onChange={e => setLedgerStartDate(e.target.value)} style={{ padding: '8px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '13px' }} />
                <span style={{ color: '#666' }}>to</span>
                <input type="date" value={ledgerEndDate} onChange={e => setLedgerEndDate(e.target.value)} style={{ padding: '8px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '13px' }} />
                <button onClick={fetchLedgerWithDates} style={{ padding: '8px 16px', background: '#667eea', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>Apply</button>
                <button onClick={() => { setLedgerStartDate(''); setLedgerEndDate(''); openProfileModal(profileEmployee); }} style={{ padding: '8px 16px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>Reset</button>
              </div>
            </div>

            {ledgerLoading ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>Loading ledger...</div>
            ) : (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h3 style={{ margin: 0, fontSize: '15px', color: '#8e44ad', fontWeight: '700' }}>Permanent Loans</h3>
                    <button onClick={() => { setLoanForm({ ...loanForm, employee_id: profileEmployee.id }); setShowAddLoan(true); }} style={{ padding: '6px 14px', background: '#8e44ad', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                      Add Loan
                    </button>
                  </div>
                  {profileLoans.length === 0 ? (
                    <div style={{ padding: '16px', background: '#f8f9fa', borderRadius: '8px', textAlign: 'center', color: '#95a5a6', fontSize: '13px' }}>No permanent loans</div>
                  ) : profileLoans.map(loan => (
                    <div key={loan.id} style={{ background: loan.status === 'settled' ? '#f0fff4' : '#fdf3ff', border: `1px solid ${loan.status === 'settled' ? '#a5d6a7' : '#ce93d8'}`, borderRadius: '8px', padding: '12px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: '700', color: '#8e44ad', fontSize: '15px' }}>{formatCurrency(loan.amount)}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{formatDate(loan.given_date)} - {loan.reason || 'No reason'}</div>
                        {loan.settled_date && <div style={{ fontSize: '11px', color: '#27ae60' }}>Settled: {formatDate(loan.settled_date)}</div>}
                      </div>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: loan.status === 'settled' ? '#d4edda' : '#f3e5f5', color: loan.status === 'settled' ? '#155724' : '#8e44ad' }}>
                          {loan.status === 'settled' ? 'Settled' : 'Active'}
                        </span>
                        {loan.status === 'active' && (
                          <button onClick={() => handleSettleLoan(loan.id)} style={{ padding: '4px 10px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>Settle</button>
                        )}
                        <button onClick={() => handleDeleteLoan(loan.id)} style={{ padding: '4px 10px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>Del</button>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '15px', color: '#27ae60', fontWeight: '700' }}>Salary History</h3>
                  {!profileLedger?.salaries?.length ? (
                    <div style={{ padding: '16px', background: '#f8f9fa', borderRadius: '8px', textAlign: 'center', color: '#95a5a6', fontSize: '13px' }}>No salary records</div>
                  ) : profileLedger.salaries.map(sal => (
                    <div key={sal.id} style={{ background: '#f0fff4', border: '1px solid #a5d6a7', borderRadius: '8px', padding: '12px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: '700', color: '#27ae60', fontSize: '15px' }}>{formatCurrency(sal.net_salary)}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{MONTHS[sal.month - 1]} {sal.year} - Paid: {formatDate(sal.date)}</div>
                        <div style={{ fontSize: '11px', color: '#888' }}>Basic: {formatCurrency(sal.basic_salary)} + Bonus: {formatCurrency(sal.bonus)} - Advance: {formatCurrency(sal.advance_deduction)}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '15px', color: '#e74c3c', fontWeight: '700' }}>Advance History</h3>
                  {!profileLedger?.advances?.length ? (
                    <div style={{ padding: '16px', background: '#f8f9fa', borderRadius: '8px', textAlign: 'center', color: '#95a5a6', fontSize: '13px' }}>No advance records</div>
                  ) : profileLedger.advances.map(adv => (
                    <div key={adv.id} style={{ background: '#fff5f5', border: '1px solid #ef9a9a', borderRadius: '8px', padding: '12px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: '700', color: '#e74c3c', fontSize: '15px' }}>{formatCurrency(adv.amount)}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{formatDate(adv.date)} - {adv.reason || 'No reason'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* MODALS */}

      {/* Add Employee Modal */}
      {showAddEmployee && (
        <div style={modalOverlay} onClick={() => setShowAddEmployee(false)}>
          <div style={modalBox} onClick={e => e.stopPropagation()}>
            <h2 style={modalTitle}>Add New Employee</h2>
            <form onSubmit={handleAddEmployee}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Full Name *</label>
                  <input style={inputStyle} type="text" placeholder="Full Name" value={employeeForm.name} onChange={e => setEmployeeForm({ ...employeeForm, name: e.target.value })} required />
                </div>
                <div>
                  <label style={labelStyle}>ID Card Number</label>
                  <input style={inputStyle} type="text" placeholder="XXXXX-XXXXXXX-X" value={employeeForm.id_card} onChange={e => setEmployeeForm({ ...employeeForm, id_card: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Phone Number</label>
                  <input style={inputStyle} type="text" placeholder="03XX-XXXXXXX" value={employeeForm.phone} onChange={e => setEmployeeForm({ ...employeeForm, phone: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Emergency Contact</label>
                  <input style={inputStyle} type="text" placeholder="Emergency contact" value={employeeForm.emergency_contact} onChange={e => setEmployeeForm({ ...employeeForm, emergency_contact: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Monthly Salary (Rs)</label>
                  <input style={inputStyle} type="number" placeholder="0" value={employeeForm.monthly_salary} onChange={e => setEmployeeForm({ ...employeeForm, monthly_salary: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Module *</label>
                  <select style={inputStyle} value={employeeForm.module} onChange={e => setEmployeeForm({ ...employeeForm, module: e.target.value })} required>
                    {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Address</label>
                  <input style={inputStyle} type="text" placeholder="Address" value={employeeForm.address} onChange={e => setEmployeeForm({ ...employeeForm, address: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Last Job</label>
                  <input style={inputStyle} type="text" placeholder="Previous employer" value={employeeForm.last_job} onChange={e => setEmployeeForm({ ...employeeForm, last_job: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Experience</label>
                  <input style={inputStyle} type="text" placeholder="e.g. 3 years" value={employeeForm.experience} onChange={e => setEmployeeForm({ ...employeeForm, experience: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Reference</label>
                  <input style={inputStyle} type="text" placeholder="Reference name" value={employeeForm.reference} onChange={e => setEmployeeForm({ ...employeeForm, reference: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Joining Date</label>
                  <input style={inputStyle} type="date" value={employeeForm.joining_date} onChange={e => setEmployeeForm({ ...employeeForm, joining_date: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Permanent Loan (Rs)</label>
                  <input style={inputStyle} type="number" placeholder="0" value={employeeForm.permanent_loan} onChange={e => setEmployeeForm({ ...employeeForm, permanent_loan: e.target.value })} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Notes</label>
                  <input style={inputStyle} type="text" placeholder="Additional notes" value={employeeForm.notes} onChange={e => setEmployeeForm({ ...employeeForm, notes: e.target.value })} />
                </div>
                {renderAllocationBlock()}
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button type="button" onClick={() => setShowAddEmployee(false)} style={cancelBtn}>Cancel</button>
                <button type="submit" style={submitBtn}>Add Employee</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Employee Modal */}
      {showEditEmployee && (
        <div style={modalOverlay} onClick={() => setShowEditEmployee(false)}>
          <div style={modalBox} onClick={e => e.stopPropagation()}>
            <h2 style={modalTitle}>Edit Employee</h2>
            <form onSubmit={handleEditEmployee}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Full Name *</label>
                  <input style={inputStyle} type="text" value={employeeForm.name} onChange={e => setEmployeeForm({ ...employeeForm, name: e.target.value })} required />
                </div>
                <div>
                  <label style={labelStyle}>ID Card Number</label>
                  <input style={inputStyle} type="text" value={employeeForm.id_card} onChange={e => setEmployeeForm({ ...employeeForm, id_card: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Phone Number</label>
                  <input style={inputStyle} type="text" value={employeeForm.phone} onChange={e => setEmployeeForm({ ...employeeForm, phone: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Emergency Contact</label>
                  <input style={inputStyle} type="text" value={employeeForm.emergency_contact} onChange={e => setEmployeeForm({ ...employeeForm, emergency_contact: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Monthly Salary (Rs)</label>
                  <input style={inputStyle} type="number" value={employeeForm.monthly_salary} onChange={e => setEmployeeForm({ ...employeeForm, monthly_salary: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Module</label>
                  <select style={inputStyle} value={employeeForm.module} onChange={e => setEmployeeForm({ ...employeeForm, module: e.target.value })}>
                    {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Address</label>
                  <input style={inputStyle} type="text" value={employeeForm.address} onChange={e => setEmployeeForm({ ...employeeForm, address: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Last Job</label>
                  <input style={inputStyle} type="text" value={employeeForm.last_job} onChange={e => setEmployeeForm({ ...employeeForm, last_job: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Experience</label>
                  <input style={inputStyle} type="text" value={employeeForm.experience} onChange={e => setEmployeeForm({ ...employeeForm, experience: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Reference</label>
                  <input style={inputStyle} type="text" value={employeeForm.reference} onChange={e => setEmployeeForm({ ...employeeForm, reference: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Joining Date</label>
                  <input style={inputStyle} type="date" value={employeeForm.joining_date} onChange={e => setEmployeeForm({ ...employeeForm, joining_date: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Status</label>
                  <select style={inputStyle} value={employeeForm.is_active} onChange={e => setEmployeeForm({ ...employeeForm, is_active: e.target.value === 'true' })}>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Notes</label>
                  <input style={inputStyle} type="text" value={employeeForm.notes} onChange={e => setEmployeeForm({ ...employeeForm, notes: e.target.value })} />
                </div>
                {renderAllocationBlock()}
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button type="button" onClick={() => setShowEditEmployee(false)} style={cancelBtn}>Cancel</button>
                <button type="submit" style={submitBtn}>Update Employee</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Advance Modal */}
      {showAddAdvance && (
        <div style={modalOverlay} onClick={() => setShowAddAdvance(false)}>
          <div style={{ ...modalBox, maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <h2 style={modalTitle}>Give Advance</h2>
            <form onSubmit={handleAddAdvance}>
              <label style={labelStyle}>Employee *</label>
              <select style={inputStyle} value={advanceForm.employee_id} onChange={e => setAdvanceForm({ ...advanceForm, employee_id: e.target.value })} required>
                <option value="">Select Employee</option>
                {employees.filter(e => e.is_active !== false).map(e => (
                  <option key={e.id} value={e.id}>{e.name} ({e.module})</option>
                ))}
              </select>
              <label style={labelStyle}>Amount (Rs) *</label>
              <input style={inputStyle} type="number" placeholder="Amount" value={advanceForm.amount} onChange={e => setAdvanceForm({ ...advanceForm, amount: e.target.value })} required />
              <label style={labelStyle}>Reason</label>
              <input style={inputStyle} type="text" placeholder="Reason for advance" value={advanceForm.reason} onChange={e => setAdvanceForm({ ...advanceForm, reason: e.target.value })} />
              <label style={labelStyle}>Date</label>
              <input style={inputStyle} type="date" value={advanceForm.given_date} onChange={e => setAdvanceForm({ ...advanceForm, given_date: e.target.value })} />
              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button type="button" onClick={() => setShowAddAdvance(false)} style={cancelBtn}>Cancel</button>
                <button type="submit" style={{ ...submitBtn, background: '#e74c3c' }}>Give Advance</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Salary Modal */}
      {showAddSalary && (
        <div style={modalOverlay} onClick={() => setShowAddSalary(false)}>
          <div style={{ ...modalBox, maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
            <h2 style={modalTitle}>Pay Salary</h2>
            <form onSubmit={handleAddSalary}>
              <label style={labelStyle}>Employee *</label>
              <select style={inputStyle} value={salaryForm.employee_id} onChange={e => {
                const emp = employees.find(em => em.id === parseInt(e.target.value));
                setSalaryForm({ ...salaryForm, employee_id: e.target.value, basic_salary: emp?.monthly_salary || '', advance_deduction: emp?.advance_balance || '' });
              }} required>
                <option value="">Select Employee</option>
                {employees.filter(e => e.is_active !== false).map(e => (
                  <option key={e.id} value={e.id}>{e.name} ({e.module}) - Balance: {formatCurrency(e.advance_balance)}</option>
                ))}
              </select>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Month *</label>
                  <select style={inputStyle} value={salaryForm.month} onChange={e => setSalaryForm({ ...salaryForm, month: e.target.value })} required>
                    {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Year *</label>
                  <input style={inputStyle} type="number" value={salaryForm.year} onChange={e => setSalaryForm({ ...salaryForm, year: e.target.value })} required />
                </div>
                <div>
                  <label style={labelStyle}>Basic Salary (Rs) *</label>
                  <input style={inputStyle} type="number" value={salaryForm.basic_salary} onChange={e => setSalaryForm({ ...salaryForm, basic_salary: e.target.value })} required />
                </div>
                <div>
                  <label style={labelStyle}>Bonus (Rs)</label>
                  <input style={inputStyle} type="number" placeholder="0" value={salaryForm.bonus} onChange={e => setSalaryForm({ ...salaryForm, bonus: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Advance Deduction (Rs)</label>
                  <input style={inputStyle} type="number" placeholder="0" value={salaryForm.advance_deduction} onChange={e => setSalaryForm({ ...salaryForm, advance_deduction: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Paid Date</label>
                  <input style={inputStyle} type="date" value={salaryForm.paid_date} onChange={e => setSalaryForm({ ...salaryForm, paid_date: e.target.value })} />
                </div>
              </div>
              <div style={{ background: '#e8f5e9', borderRadius: '8px', padding: '12px', marginTop: '12px', textAlign: 'center' }}>
                <span style={{ fontSize: '13px', color: '#555' }}>Net Salary Preview: </span>
                <strong style={{ color: '#27ae60', fontSize: '16px' }}>
                  {formatCurrency((parseFloat(salaryForm.basic_salary || 0) + parseFloat(salaryForm.bonus || 0) - parseFloat(salaryForm.advance_deduction || 0)))}
                </strong>
              </div>
              <label style={{ ...labelStyle, marginTop: '12px' }}>Notes</label>
              <input style={inputStyle} type="text" placeholder="Notes" value={salaryForm.notes} onChange={e => setSalaryForm({ ...salaryForm, notes: e.target.value })} />
              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button type="button" onClick={() => setShowAddSalary(false)} style={cancelBtn}>Cancel</button>
                <button type="submit" style={{ ...submitBtn, background: '#27ae60' }}>Pay Salary</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Permanent Loan Modal */}
      {showAddLoan && (
        <div style={modalOverlay} onClick={() => setShowAddLoan(false)}>
          <div style={{ ...modalBox, maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <h2 style={modalTitle}>Add Permanent Loan</h2>
            <p style={{ fontSize: '13px', color: '#7f8c8d', marginTop: '-12px', marginBottom: '16px' }}>This loan will only be settled when the employee leaves the company.</p>
            <form onSubmit={handleAddLoan}>
              <label style={labelStyle}>Employee *</label>
              <select style={inputStyle} value={loanForm.employee_id} onChange={e => setLoanForm({ ...loanForm, employee_id: e.target.value })} required>
                <option value="">Select Employee</option>
                {employees.filter(e => e.is_active !== false).map(e => (
                  <option key={e.id} value={e.id}>{e.name} ({e.module})</option>
                ))}
              </select>
              <label style={labelStyle}>Loan Amount (Rs) *</label>
              <input style={inputStyle} type="number" placeholder="Amount" value={loanForm.amount} onChange={e => setLoanForm({ ...loanForm, amount: e.target.value })} required />
              <label style={labelStyle}>Reason</label>
              <input style={inputStyle} type="text" placeholder="Reason for loan" value={loanForm.reason} onChange={e => setLoanForm({ ...loanForm, reason: e.target.value })} />
              <label style={labelStyle}>Date</label>
              <input style={inputStyle} type="date" value={loanForm.given_date} onChange={e => setLoanForm({ ...loanForm, given_date: e.target.value })} />
              <label style={labelStyle}>Notes</label>
              <input style={inputStyle} type="text" placeholder="Additional notes" value={loanForm.notes} onChange={e => setLoanForm({ ...loanForm, notes: e.target.value })} />
              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button type="button" onClick={() => setShowAddLoan(false)} style={cancelBtn}>Cancel</button>
                <button type="submit" style={{ ...submitBtn, background: '#8e44ad' }}>Record Loan</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const modalOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', overflowY: 'auto' };
const modalBox = { background: 'white', padding: '30px', borderRadius: '12px', width: '100%', maxWidth: '650px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' };
const modalTitle = { fontSize: '20px', fontWeight: '700', marginBottom: '20px', color: '#2c3e50', marginTop: 0 };
const labelStyle = { display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '6px', marginTop: '4px' };
const inputStyle = { width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '4px' };
const cancelBtn = { flex: 1, padding: '12px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' };
const submitBtn = { flex: 1, padding: '12px', background: '#667eea', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' };

export default HRDashboard;

